import { useEffect, useMemo, useState } from "react"
import { Trash2Icon } from "lucide-react"
import { usePage } from "@/components/page/hooks"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Combobox } from "@/components/ui/combobox"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { supabase } from "@/lib/data"
import { countriesList, idToDomain } from "@/lib/agents/search/countries"

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  sources: Source[]
  onChange: (sources: Source[]) => void
}

interface Entry {
  id: number
  name: string
}

const ENGINES: { value: VectorSearchEngines, label: string }[] = [
  { value: "supabase", label: "Documents" },
  { value: "supabased", label: "Documents by domain" },
  { value: "services", label: "Services" },
  { value: "exa", label: "Exa" },
  { value: "weaviate", label: "Weaviate" },
  { value: "jina", label: "Jina" },
  { value: "databricks", label: "Databricks" },
  { value: "youtube", label: "YouTube" },
  { value: "zendesk", label: "Zendesk" },
  { value: "rescuenet", label: "Rescue.net" },
  { value: "box", label: "Box.net" },
]

const DOMAIN: { [engine: string]: string } = {
  weaviate: "Domain",
  exa: "Domain, e.g. google.com",
  youtube: "Channel",
  zendesk: "Zendesk domain",
  box: "Folder URL",
}

export function SearchDialog({ open, onOpenChange, title, sources, onChange }: Props) {
  const { team } = usePage()
  const [documents, setDocuments] = useState<Entry[]>([])
  const [domains, setDomains] = useState<Entry[]>([])

  useEffect(() => {
    if (!open || !team?.id) return
    supabase.from("vectors").select("id, name").eq("isChunk", false).eq("team", team.id).order("name").then(({ data }) => setDocuments((data ?? []) as Entry[]))
    supabase.from("domains").select("id, name").eq("team", team.id).order("name").then(({ data }) => setDomains((data ?? []) as Entry[]))
  }, [open, team?.id])

  function update(index: number, patch: Partial<Source>) {
    onChange(sources.map((s, i) => i === index ? { ...s, ...patch } : s))
  }

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{title || "Search"}</DialogTitle>
        <DialogDescription>Every source is searched with the same query and the results are joined</DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-4">
        {sources.map((source, index) => <div key={index} className="rounded-md border p-3 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Select value={source.type} onValueChange={type => onChange(sources.map((s, i) => i === index ? { type: type as VectorSearchEngines, results: s.results } : s))}>
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>{ENGINES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
            </Select>
            <Label className="text-xs text-muted-foreground">Results</Label>
            <Input type="number" min={1} max={100} value={source.results ?? 5} onChange={e => update(index, { results: Number(e.target.value) || undefined })} className="w-20" />
            <div className="flex-1" />
            <Button variant="ghost" size="icon-sm" aria-label="Remove source" onClick={() => onChange(sources.filter((_, i) => i !== index))}><Trash2Icon /></Button>
          </div>

          {source.type === "supabase" && <PickList entries={documents} selected={source.sources ?? []} onChange={ids => update(index, { sources: ids })} what="documents" />}
          {source.type === "supabased" && <PickList entries={domains} selected={source.sources ?? []} onChange={ids => update(index, { sources: ids })} what="domains" />}

          {(source.type === "supabase" || source.type === "supabased") && <Label className="text-sm gap-2">
            <Checkbox checked={!!source.chunked} onCheckedChange={checked => update(index, { chunked: !!checked || undefined })} />Search the chunks instead of whole documents
          </Label>}

          {(source.type === "supabase" || source.type === "weaviate") && <Input value={source.locale ?? ""} onChange={e => update(index, { locale: e.target.value || undefined })} placeholder="Locale, e.g. en" />}
          {DOMAIN[source.type] && <Input value={source.domain ?? ""} onChange={e => update(index, { domain: e.target.value || undefined })} placeholder={DOMAIN[source.type]} />}
          {source.type === "zendesk" && <Input value={source.baseUrl ?? ""} onChange={e => update(index, { baseUrl: e.target.value || undefined })} placeholder="Base URL, e.g. https://mycompany.zendesk.com" />}
          {source.type === "jina" && <Input value={source.url ?? ""} onChange={e => update(index, { url: e.target.value || undefined })} placeholder="https://example.com" />}

          {source.type === "databricks" && <Select value={source.domain ?? ""} onValueChange={domain => update(index, { domain })}>
            <SelectTrigger><SelectValue placeholder="Select an index" /></SelectTrigger>
            <SelectContent>
              {["signpost_docs_vector", "signpost_docs_llama_vector", "signpost"].map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>}

          {source.type === "services" && <Combobox
            items={countriesList.map(c => ({ value: idToDomain[c.value], label: c.label }))}
            value={source.domain}
            onChange={domain => update(index, { domain })}
            placeholder="Search country..."
          />}
        </div>)}
        <Button variant="outline" size="sm" className="self-start" onClick={() => onChange([...sources, { type: "supabase", results: 5 }])}>Add source</Button>
      </div>
    </DialogContent>
  </Dialog>
}

interface PickListProps {
  entries: Entry[]
  selected: number[]
  onChange: (ids: number[]) => void
  what: string
}

function PickList({ entries, selected, onChange, what }: PickListProps) {
  const [filter, setFilter] = useState("")
  const [onlySelected, setOnlySelected] = useState(false)
  const chosen = useMemo(() => new Set(selected), [selected])
  const shown = entries.filter(e => e.name?.toLowerCase().includes(filter.toLowerCase()) && (!onlySelected || chosen.has(e.id)))

  function toggle(id: number, on: boolean) {
    onChange(on ? [...selected, id] : selected.filter(s => s !== id))
  }

  function selectShown(on: boolean) {
    const ids = new Set(shown.map(e => e.id))
    onChange(on ? [...new Set([...selected, ...ids])] : selected.filter(s => !ids.has(s)))
  }

  return <div className="flex flex-col gap-2">
    <div className="flex items-center gap-2">
      <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder={`Filter ${what}`} className="flex-1" />
      <Button variant="outline" size="sm" onClick={() => selectShown(true)}>Select shown</Button>
      <Button variant="outline" size="sm" onClick={() => selectShown(false)}>Clear shown</Button>
    </div>
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span>{selected.length} of {entries.length} {what} selected</span>
      <div className="flex-1" />
      <Label className="text-xs gap-2"><Checkbox checked={onlySelected} onCheckedChange={checked => setOnlySelected(!!checked)} />Only selected</Label>
    </div>
    <div className="max-h-64 overflow-y-auto rounded-md border divide-y">
      {shown.map(e => <Label key={e.id} className="flex items-center gap-2 px-2 py-1.5 text-sm font-normal hover:bg-muted/50">
        <Checkbox checked={chosen.has(e.id)} onCheckedChange={checked => toggle(e.id, !!checked)} />
        <span className="truncate">{e.name}</span>
      </Label>)}
      {!shown.length && <div className="px-2 py-4 text-center text-sm text-muted-foreground">No {what}</div>}
    </div>
  </div>
}
